import { useEffect, useMemo, useState } from 'react';

import { useStores } from '@/components/hooks';
import type { FilledRecipientEntry, MissingRecipientEntry } from './types';

interface UseMissingDetailsStateParams {
    showPhones: boolean;
    showPasswords: boolean;
}

interface UseMissingDetailsStateResult {
    detailsLoaded: boolean;
    isMissing: (email: string) => boolean;
    missingRecipients: MissingRecipientEntry[];
    filledRecipients: FilledRecipientEntry[];
}

const useMissingDetailsState = ({
    showPhones,
    showPasswords,
}: UseMissingDetailsStateParams): UseMissingDetailsStateResult => {
    const { sharedUsersStore, usersPhonesStore, usersListStore } = useStores();
    const { recipientsList } = sharedUsersStore;

    const hasMissingDetails = showPhones || showPasswords;

    const userRecipients = useMemo(
        () => recipientsList.filter((r) => r.type === 'user'),
        [recipientsList],
    );
    const groupRecipients = useMemo(
        () => recipientsList.filter((r) => r.type === 'group'),
        [recipientsList],
    );

    const [detailsLoaded, setDetailsLoaded] = useState(false);

    useEffect(() => {
        usersPhonesStore.setPhoneNumbersGroupId(null);
    }, []);

    useEffect(() => {
        if (!hasMissingDetails) {
            setDetailsLoaded(true);
            return undefined;
        }

        let cancelled = false;
        setDetailsLoaded(false);

        (async (): Promise<void> => {
            const userPhonePromises = showPhones
                ? userRecipients.map(({ email }) => usersPhonesStore.fetchUserPhone(email))
                : [];

            const groupMemberEmails: string[] = [];
            // eslint-disable-next-line no-restricted-syntax
            for (const { id } of groupRecipients) {
                // eslint-disable-next-line no-await-in-loop
                await usersListStore.fetchUsersCustomGroup(id);
                const group = usersListStore.userGroupsList.find((g) => g.groupId === id);
                (group?.members || []).forEach(({ email }) => groupMemberEmails.push(email));
            }

            const groupPhonePromises = showPhones
                ? groupMemberEmails.map((email) => usersPhonesStore.fetchUserPhone(email))
                : [];

            await Promise.all([...userPhonePromises, ...groupPhonePromises]);

            if (showPasswords) {
                await Promise.all(
                    groupRecipients.map(({ id }) => sharedUsersStore.checkGroupMembersPasswords(id)),
                );
            }

            if (!cancelled) setDetailsLoaded(true);
        })();

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipientsList.map((r) => r.id).join(','), showPhones, showPasswords]);

    const isMissing = (email: string): boolean => {
        if (showPhones) {
            const phone = usersPhonesStore.getUserPhone(email)?.phonesVector?.currentPhone;
            if (!phone?.phone) return true;
        }
        if (showPasswords) {
            if (sharedUsersStore.getRecipientPasswordInfo(email)?.hasPassword === false) return true;
        }
        return false;
    };

    const missingRecipients: MissingRecipientEntry[] = useMemo(() => {
        if (!detailsLoaded || !hasMissingDetails) return [];
        const result: MissingRecipientEntry[] = [];

        userRecipients.forEach((r) => {
            if (isMissing(r.email)) result.push({ email: r.email, recipientId: r.id });
        });

        groupRecipients.forEach(({ id }) => {
            const group = usersListStore.userGroupsList.find((g) => g.groupId === id);
            if (!group) return;
            (group.members || []).forEach(({ email }) => {
                if (isMissing(email)) result.push({ email, groupName: group.groupName, groupId: id });
            });
        });

        return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        detailsLoaded,
        hasMissingDetails,
        showPhones,
        showPasswords,
        recipientsList.map((r) => r.id).join(','),
        usersPhonesStore.usersPhonesMap.size,
        sharedUsersStore.recipientsPasswordsMap.size,
    ]);

    const filledRecipients: FilledRecipientEntry[] = useMemo(() => {
        if (!detailsLoaded || !hasMissingDetails) return [];
        const result: FilledRecipientEntry[] = [];

        userRecipients.forEach((r) => {
            if (!isMissing(r.email)) {
                result.push({ type: 'user', email: r.email });
            }
        });

        groupRecipients.forEach(({ id }) => {
            const group = usersListStore.userGroupsList.find((g) => g.groupId === id);
            if (group) {
                result.push({ type: 'group', groupId: id, groupName: group.groupName });
            }
        });

        return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        detailsLoaded,
        hasMissingDetails,
        showPhones,
        showPasswords,
        recipientsList.map((r) => r.id).join(','),
        usersPhonesStore.usersPhonesMap.size,
        sharedUsersStore.recipientsPasswordsMap.size,
    ]);

    return {
        detailsLoaded,
        isMissing,
        missingRecipients,
        filledRecipients,
    };
};

export default useMissingDetailsState;
